/**
 * Bad Entrepreneur parallax.
 *
 * Moves media slower than the page while scrolling:
 * - The homepage hero (.hero__media-grid > .hero__media), replacing the old inline script.
 * - Any section marked [data-be-parallax="<speed>"] containing [data-be-parallax-media].
 *
 * Only transforms are animated (no layout work), updates are batched with
 * requestAnimationFrame, off-screen elements are skipped, and everything is disabled for
 * visitors who prefer reduced motion. Re-scans after theme editor section changes.
 */
(() => {
  if (window.__beParallaxInit) return;
  window.__beParallaxInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HERO_SPEED = 0.3;
  /** @type {{ container: Element, media: HTMLElement, speed: number, hero: boolean }[]} */
  let items = [];
  let ticking = false;

  function collect() {
    items = [];

    for (const container of document.querySelectorAll('[data-be-parallax]')) {
      const media = container.querySelector('[data-be-parallax-media]');
      if (!(media instanceof HTMLElement)) continue;
      const speed = parseFloat(container.getAttribute('data-be-parallax') || '0.15');
      items.push({ container, media, speed: Number.isFinite(speed) ? speed : 0.15, hero: false });
    }

    for (const container of document.querySelectorAll('.hero__media-grid')) {
      const media = container.querySelector('.hero__media');
      if (!(media instanceof HTMLElement)) continue;
      items.push({ container, media, speed: HERO_SPEED, hero: true });
    }
  }

  function reset() {
    for (const item of items) item.media.style.transform = '';
  }

  function update() {
    ticking = false;
    if (reduceMotion.matches) {
      reset();
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    for (const item of items) {
      const rect = item.container.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewportHeight) continue;

      let offset;
      if (item.hero) {
        // Hero starts at the top of the page: drift down as it scrolls away.
        offset = Math.min(rect.height * 0.25, Math.max(0, -rect.top * item.speed));
      } else {
        // Bands: centered at rest when the band is in the middle of the viewport.
        const fromCenter = rect.top + rect.height / 2 - viewportHeight / 2;
        const limit = rect.height * 0.075;
        offset = Math.max(-limit, Math.min(limit, -fromCenter * item.speed));
      }

      item.media.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    }
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function refresh() {
    reset();
    collect();
    requestUpdate();
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  reduceMotion.addEventListener('change', requestUpdate);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', () => window.requestAnimationFrame(refresh));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();
